/** Spawns and animates floating damage number popups in world space. */
import Phaser from 'phaser';
import { ConfigLoader } from './ConfigLoader';
import { DebugConfig } from '../debug/DebugConfig';
import { randomInRange } from '../utils/Math';
import { crispText } from '../ui/CrispUtil';
import { Font, Hex, worldShadow } from '../ui/Theme';

interface DamagePopup {
  text: Phaser.GameObjects.Text;
  tweens: Phaser.Tweens.Tween[];
}

/** Creates and manages floating damage number text popups that animate upward and fade out. */
export class DamageNumbers {
  private scene: Phaser.Scene;
  private popups: DamagePopup[] = [];

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  /** Compute proportional font size from the ratio of final damage to base damage. */
  private computeFontSize(damage: number, baseDamage: number): string {
    const cfg = ConfigLoader.combat.damageNumbers;
    const ratio = baseDamage > 0 ? damage / baseDamage : 1;
    const raw = cfg.baseFontSizePx + (ratio - 1) * cfg.fontScalePerRatio;
    const clamped = Math.max(cfg.minFontSizePx, Math.min(cfg.maxFontSizePx, Math.round(raw)));
    return `${clamped}px`;
  }

  /** Shared float animation: rise, fade, zigzag. */
  private animatePopup(text: Phaser.GameObjects.Text, worldX: number, worldY: number): void {
    const cfg = ConfigLoader.combat.damageNumbers;
    const offsetX = randomInRange(-cfg.randomOffsetRange / 2, cfg.randomOffsetRange / 2);
    const debugActive = DebugConfig.anyActive;
    const floatDistance = debugActive ? cfg.floatDistance * 2 : cfg.floatDistance;
    const floatDuration = debugActive ? cfg.floatDurationMs * 2.5 : cfg.floatDurationMs;

    // Random zigzag direction so adjacent floaties weave apart
    const zigzagSign = Math.random() < 0.5 ? -1 : 1;
    const zigzagPhase = Math.random() * Math.PI * 2;
    const baseX = worldX + offsetX;

    // Vertical rise + fade
    const riseTween = this.scene.tweens.add({
      targets: text,
      y: worldY - 8 - floatDistance,
      alpha: 0,
      duration: floatDuration,
      ease: 'Cubic.easeOut',
      onComplete: () => {
        text.destroy();
        this.popups = this.popups.filter(p => p.text !== text);
      },
    });

    // Horizontal zigzag driven by a counter so X follows a sine wave
    const zigzagTween = this.scene.tweens.addCounter({
      from: 0,
      to: 1,
      duration: floatDuration,
      onUpdate: (tween) => {
        const t = tween.getValue() ?? 0;
        const wave = Math.sin(zigzagPhase + t * cfg.zigzagCycles * Math.PI * 2);
        // Amplitude decays with the rise so the tail end drifts gently
        const amp = cfg.zigzagAmplitude * zigzagSign * (1 - t);
        text.setX(baseX + wave * amp);
      },
    });

    this.popups.push({ text, tweens: [riseTween, zigzagTween] });
  }

  /** Spawn a damage number popup at the given world position with direction-based color and proportional sizing. */
  spawn(worldX: number, worldY: number, damage: number, baseDamage: number, isCrit: boolean, isBackstab = false, received = false): void {
    const cfg = ConfigLoader.combat.damageNumbers;
    const color = received ? cfg.receivedColor : cfg.dealtColor;
    const fontSize = this.computeFontSize(damage, baseDamage);
    const text = crispText(this.scene, worldX, worldY - 8, `${damage}`, {
      fontFamily: Font.family,
      fontSize,
      color,
      stroke: Hex.black,
      strokeThickness: 3,
      shadow: worldShadow,
    }).setOrigin(0.5).setDepth(2001);

    this.animatePopup(text, worldX, worldY);
  }

  /** Spawn a generic text label popup (e.g. "STAGGERED") at the given world position, delayed to separate from damage numbers. */
  spawnLabel(worldX: number, worldY: number, label: string, color: string, fontSize: string): void {
    const cfg = ConfigLoader.combat.damageNumbers;

    this.scene.time.delayedCall(cfg.labelDelayMs, () => {
      const text = crispText(this.scene, worldX, worldY - 8, label, {
        fontFamily: Font.family,
        fontSize,
        color,
        stroke: Hex.black,
        strokeThickness: 3,
        shadow: worldShadow,
      }).setOrigin(0.5).setDepth(2001);

      this.animatePopup(text, worldX, worldY);
    });
  }

  /** Stop all in-flight popups and destroy their text objects. */
  destroy(): void {
    for (const popup of this.popups) {
      for (const tween of popup.tweens) tween.stop();
      popup.text.destroy();
    }
    this.popups = [];
  }
}
